import * as React from 'react';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Skeleton from '@mui/material/Skeleton';
import Divider from '@mui/material/Divider';
import Chip from '@mui/material/Chip';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import dayjs from 'dayjs';
import { api, type ApiAccount, type ApiTransaction } from '../services/api';

function formatCurrency(cents: number, currency = 'USD') {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(cents / 100);
}

type DashboardData = {
  netWorthCents: number;
  totalAssetsCents: number;
  totalLiabilitiesCents: number;
  accounts: ApiAccount[];
  recentTransactions: ApiTransaction[];
  monthIncome: number;
  monthExpenses: number;
};

export default function HomePage() {
  const [data, setData] = React.useState<DashboardData | null>(null);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [netWorthRes, accountsRes, txRes] = await Promise.all([
          api.getNetWorth(),
          api.getAccounts('active'),
          api.getTransactions({ limit: 10, sort: 'date', direction: 'desc' }),
        ]);

        // Compute this month's income/expenses from recent transactions
        const now = dayjs();
        const monthStart = now.startOf('month').toISOString();
        const monthTxRes = await api.getTransactions({
          limit: 500,
          startDate: monthStart,
          sort: 'date',
          direction: 'desc',
        });
        let monthIncome = 0;
        let monthExpenses = 0;
        for (const tx of monthTxRes.transactions) {
          if (tx.isTransfer) continue; // skip internal transfers
          if (tx.amountCents > 0) monthIncome += tx.amountCents;
          else monthExpenses += Math.abs(tx.amountCents);
        }

        if (!cancelled) {
          setData({
            netWorthCents: netWorthRes.netWorthCents,
            totalAssetsCents: netWorthRes.totalAssetsCents,
            totalLiabilitiesCents: netWorthRes.totalLiabilitiesCents,
            accounts: accountsRes.accounts,
            recentTransactions: txRes.transactions,
            monthIncome,
            monthExpenses,
          });
        }
      } catch (err) {
        console.error('Dashboard load error:', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return (
      <Box
        sx={{
          width: '100%',
          height: '100%',
          minHeight: 0,
          overflowY: 'auto',
          overflowX: 'hidden',
          boxSizing: 'border-box',
          maxWidth: 1700,
          p: 2,
        }}
      >
        <Skeleton variant="text" width={200} height={40} />
        <Grid container spacing={2} sx={{ mt: 1 }}>
          {[...Array(4)].map((_, i) => (
            <Grid key={i} size={{ xs: 12, sm: 6, lg: 3 }}>
              <Skeleton variant="rounded" height={120} />
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  }

  if (!data) {
    return (
      <Box
        sx={{
          width: '100%',
          height: '100%',
          minHeight: 0,
          overflowY: 'auto',
          overflowX: 'hidden',
          boxSizing: 'border-box',
          p: 3,
        }}
      >
        <Typography color="error">Failed to load dashboard data.</Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        minHeight: 0,
        overflowY: 'auto',
        overflowX: 'hidden',
        boxSizing: 'border-box',
        maxWidth: 1700,
        p: { xs: 1, sm: 2 },
      }}
    >
      <Typography component="h2" variant="h6" sx={{ mb: 2 }}>
        Overview
      </Typography>

      {/* Summary cards */}
      <Grid container spacing={2} columns={12} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <Paper sx={{ p: 2.5, height: '100%' }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
              <AccountBalanceIcon fontSize="small" color="primary" />
              <Typography variant="body2" color="text.secondary">Net Worth</Typography>
            </Stack>
            <Typography variant="h5" fontWeight={600}>
              {formatCurrency(data.netWorthCents)}
            </Typography>
            <Stack direction="row" spacing={2} sx={{ mt: 1 }}>
              <Typography variant="caption" color="success.main">
                Assets: {formatCurrency(data.totalAssetsCents)}
              </Typography>
              <Typography variant="caption" color="error.main">
                Debt: {formatCurrency(data.totalLiabilitiesCents)}
              </Typography>
            </Stack>
          </Paper>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <Paper sx={{ p: 2.5, height: '100%' }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
              <TrendingUpIcon fontSize="small" color="success" />
              <Typography variant="body2" color="text.secondary">Income This Month</Typography>
            </Stack>
            <Typography variant="h5" fontWeight={600} color="success.main">
              {formatCurrency(data.monthIncome)}
            </Typography>
          </Paper>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <Paper sx={{ p: 2.5, height: '100%' }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
              <TrendingDownIcon fontSize="small" color="error" />
              <Typography variant="body2" color="text.secondary">Expenses This Month</Typography>
            </Stack>
            <Typography variant="h5" fontWeight={600} color="error.main">
              {formatCurrency(data.monthExpenses)}
            </Typography>
          </Paper>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <Paper sx={{ p: 2.5, height: '100%' }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
              <ReceiptLongIcon fontSize="small" color="info" />
              <Typography variant="body2" color="text.secondary">Accounts</Typography>
            </Stack>
            <Typography variant="h5" fontWeight={600}>
              {data.accounts.length}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Active accounts
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Two-column layout: Accounts + Recent Transactions */}
      <Grid container spacing={2} columns={12}>
        {/* Accounts */}
        <Grid size={{ xs: 12, lg: 5 }}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1.5 }}>
              Accounts
            </Typography>
            <Stack spacing={1} divider={<Divider />}>
              {data.accounts.slice(0, 8).map((acct) => (
                <Stack key={acct.id} direction="row" justifyContent="space-between" alignItems="center">
                  <Box>
                    <Typography variant="body2" fontWeight={500}>{acct.name}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {acct.institution || acct.type}
                    </Typography>
                  </Box>
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    color={acct.balanceCents >= 0 ? 'text.primary' : 'error.main'}
                  >
                    {formatCurrency(acct.balanceCents, acct.currency)}
                  </Typography>
                </Stack>
              ))}
              {data.accounts.length > 8 && (
                <Typography variant="caption" color="text.secondary" textAlign="center">
                  +{data.accounts.length - 8} more accounts
                </Typography>
              )}
              {data.accounts.length === 0 && (
                <Typography variant="body2" color="text.secondary">
                  No accounts yet. Add one from the Accounts page.
                </Typography>
              )}
            </Stack>
          </Paper>
        </Grid>

        {/* Recent Transactions */}
        <Grid size={{ xs: 12, lg: 7 }}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1.5 }}>
              Recent Transactions
            </Typography>
            <Stack spacing={1} divider={<Divider />}>
              {data.recentTransactions.map((tx) => (
                <Stack key={tx.id} direction="row" justifyContent="space-between" alignItems="center">
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography variant="body2" fontWeight={500} noWrap>
                      {tx.description}
                    </Typography>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography variant="caption" color="text.secondary">
                        {dayjs(tx.occurredAt).format('MMM D')}
                      </Typography>
                      {tx.category && (
                        <Chip label={tx.category.name} size="small" variant="outlined" sx={{ height: 18, fontSize: 11 }} />
                      )}
                    </Stack>
                  </Box>
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    color={tx.amountCents < 0 ? 'error.main' : 'success.main'}
                    sx={{ ml: 2, flexShrink: 0 }}
                  >
                    {tx.amountCents < 0 ? '-' : '+'}
                    {formatCurrency(Math.abs(tx.amountCents), tx.currency)}
                  </Typography>
                </Stack>
              ))}
              {data.recentTransactions.length === 0 && (
                <Typography variant="body2" color="text.secondary">
                  No transactions yet.
                </Typography>
              )}
            </Stack>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}
